import moment from 'moment';
import isNil from 'lodash/isNil';

import { FORM_INFO } from './constants';
import type { PassbookRate } from '../../ViewProperty/AffordableSetup/Programs/shared';
// This is added currently for Washington State requirements for their asset forms
export const hasInterestRateField = (property) => {
  const physicalState = property?.physicalState ?? '';
  const isWashingtonState = physicalState === 'US-WA';
  return isWashingtonState;
};

// The asset confirmation checkbox is always shown (FES-548). The backend only marks
// the asset section complete once the resident explicitly confirms it when the program
// is HOTMA-active OR auto-rolls over income and assets, OR when the
// require-income-asset-confirmation flag makes the confirmation required on every
// program (FES-559). In those cases Save is hard-gated on the confirmation; otherwise
// the section is considered reviewed on save. Keeping this in one place ensures the
// initial reviewed state and the Save gate agree with the backend completion rule.
export const requiresAssetConfirmation = (
  isHotmaActive: boolean,
  autoRolloverIncomeAndAssets: boolean,
  requireIncomeAssetConfirmation: boolean,
): boolean =>
  Boolean(isHotmaActive) ||
  Boolean(autoRolloverIncomeAndAssets) ||
  Boolean(requireIncomeAssetConfirmation);

export const getInitialAssetFormReviewed = ({
  isHotmaActive,
  autoRolloverIncomeAndAssets,
  requireIncomeAssetConfirmation,
  assetConfirmation,
  wasFormEdited,
}: {
  isHotmaActive: boolean,
  autoRolloverIncomeAndAssets: boolean,
  requireIncomeAssetConfirmation: boolean,
  assetConfirmation: ?boolean,
  wasFormEdited: boolean,
}): boolean => {
  if (
    !requiresAssetConfirmation(
      isHotmaActive,
      autoRolloverIncomeAndAssets,
      requireIncomeAssetConfirmation,
    )
  ) {
    return true;
  }
  if (isNil(assetConfirmation) || wasFormEdited) {
    return false;
  }
  return assetConfirmation;
};

export const getAssetDefinitions = (isHotmaActive) => {
  let allAssetDefinitions = [...FORM_INFO.initialAssets];
  if (!isHotmaActive) {
    allAssetDefinitions = [
      ...allAssetDefinitions,
      ...FORM_INFO.retirementAccounts,
    ];
  }
  allAssetDefinitions = [
    ...allAssetDefinitions,
    ...FORM_INFO.lifeInsurance,
    ...FORM_INFO.finalAssets,
  ];
  return allAssetDefinitions;
};

export const getPassbookRateByProgramAndDate = (
  data: Array<PassbookRate>,
  programName: string,
  effectiveDate: string,
): number => {
  return (
    data?.find((rate: PassbookRate) => {
      const mRateStartDate = moment(rate.startDate);
      const mRateEndDate = isNil(rate.endDate)
        ? moment().add('100', 'years')
        : moment(rate.endDate);
      const mEffectiveDate = moment(effectiveDate);
      return (
        rate.programName === programName &&
        mEffectiveDate.isBetween(mRateStartDate, mRateEndDate, undefined, '[]')
      );
    })?.amount ?? 0
  );
};
